import { CalendarIcon, TriangleDownIcon } from "@primer/octicons-react";
import { Box, Button, SelectPanel, Text } from "@primer/react";
import { useMemo, useState } from "react";
import type { IRegistrationPeriod } from "../api/registration/registrationApi";

interface IComboboxDotDangKyProps {
    /** Danh sách đợt đăng ký SV thuộc (từ registrationApi.GetPeriods). */
    periods: IRegistrationPeriod[];
    /** Id đợt đang chọn. */
    value: number;
    /** Đổi đợt — nhận periodId mới. */
    onChange: (periodId: number) => void;
    /** Ẩn nhãn "Đợt đăng ký" bên trái (mặc định hiện). */
    hideLabel?: boolean;
}

/**
 * Combobox chọn ĐỢT ĐĂNG KÝ — style giống các combobox bên ESS_TKB
 * (SelectPanel + anchor Button có TriangleDownIcon + ô tìm kiếm).
 * Dùng chung cho cổng SV (mobile + desktop) khi SV thuộc nhiều đợt mở.
 */
const ComboboxDotDangKy = (props: IComboboxDotDangKyProps) => {
    const { periods, value, onChange, hideLabel } = props;
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("");

    const items = useMemo(
        () => periods.map(p => ({ id: p.periodId, text: p.label })),
        [periods]
    );
    const selectedItem = useMemo(
        () => items.find(i => i.id === value),
        [items, value]
    );
    const filteredItems = useMemo(
        () => items.filter(i => i.text.toLowerCase().includes(filter.toLowerCase())),
        [items, filter]
    );

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                p: 2,
                bg: "accent.subtle",
                border: "1px solid",
                borderColor: "accent.muted",
                borderRadius: 2,
            }}
        >
            {!hideLabel && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "accent.fg" }}>
                    <CalendarIcon size={16} />
                    <Text sx={{ fontWeight: 600, fontSize: 1, whiteSpace: "nowrap" }}>
                        Đợt đăng ký
                    </Text>
                </Box>
            )}
            <SelectPanel
                renderAnchor={({ children, "aria-labelledby": ariaLabelledBy, ...anchorProps }) => (
                    <Button
                        trailingAction={TriangleDownIcon}
                        aria-labelledby={` ${ariaLabelledBy}`}
                        {...anchorProps}
                        sx={{ minWidth: 240 }}
                    >
                        <Box
                            as="span"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                            }}
                        >
                            {selectedItem ? selectedItem.text : "Chọn đợt đăng ký"}
                        </Box>
                    </Button>
                )}
                title="Chọn đợt đăng ký"
                placeholderText="Nhập từ khóa tìm kiếm"
                open={open}
                onOpenChange={setOpen}
                items={filteredItems}
                selected={selectedItem}
                onSelectedChange={(item: any) => {
                    if (item?.id) onChange(item.id);
                }}
                onFilterChange={setFilter}
                showItemDividers
                overlayProps={{ width: "medium", height: "small" }}
            />
        </Box>
    );
};

export default ComboboxDotDangKy;
